/**
 * Sync Dialogs for GameDetail
 * - Manual Sync Dialog
 * - Score Mismatch Dialog
 * - Xbox Title Search Dialog
 * - PSN Title Search Dialog
 */
import { useState, useMemo } from "react";
import api from "@/lib/api";
import { 
  Loader2, 
  Check, 
  Search, 
  Trophy,
  Gamepad2,
  Square,
  CheckSquare,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatTime12Hour } from "./AchievementCard";

// Auto-format date input
const formatDateInput = (inputValue) => {
  let digits = inputValue.replace(/\D/g, '');
  digits = digits.slice(0, 8);
  let formatted = '';
  if (digits.length > 0) {
    formatted = digits.slice(0, 2);
  }
  if (digits.length > 2) {
    formatted += '/' + digits.slice(2, 4);
  }
  if (digits.length > 4) {
    formatted += '/' + digits.slice(4, 8);
  }
  return formatted;
};

// Auto-format time input
const formatTimeInput = (inputValue) => {
  const sanitized = inputValue.replace(/[^0-9:]/g, '');
  if (sanitized.includes(':')) {
    const parts = sanitized.split(':');
    const hours = parts[0].slice(0, 2);
    const minutes = parts[1]?.slice(0, 2) || '';
    return minutes ? `${hours}:${minutes}` : hours;
  }
  const digits = sanitized.replace(/:/g, '');
  if (digits.length <= 2) return digits;
  if (digits.length === 3) {
    // Check if first 2 digits could be a valid hour (00-23)
    const firstTwo = parseInt(digits.slice(0, 2), 10);
    if (firstTwo <= 23) {
      // Format as HH:M (e.g., "210" → "21:0", "143" → "14:3")
      return `${digits.slice(0, 2)}:${digits[2]}`;
    }
    // Otherwise format as H:MM (e.g., "930" → "9:30")
    return `${digits[0]}:${digits.slice(1)}`;
  }
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
};

/**
 * Manual Sync Dialog
 */
export function ManualSyncDialog({
  open,
  onOpenChange,
  achievements,
  selections,
  onSelectionsChange,
  onSelectAll,
  onDeselectAll,
  onToggleAchievement,
  onSave,
  saving,
}) {
  const [collapsed, setCollapsed] = useState({});
  // Sorting controls for the bulk entry list.
  const [sortMode, setSortMode] = useState("status"); // status | name | unlockedFirst | lockedFirst
  const [sortDlcByName, setSortDlcByName] = useState(false);

  const nameCompare = (a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" });
  const sortWithin = (list) => {
    const arr = [...list];
    if (sortMode === "name") arr.sort(nameCompare);
    else if (sortMode === "unlockedFirst") arr.sort((a, b) => (b.isUnlocked === true) - (a.isUnlocked === true) || nameCompare(a, b));
    else if (sortMode === "lockedFirst") arr.sort((a, b) => (a.isUnlocked === true) - (b.isUnlocked === true) || nameCompare(a, b));
    // "status" keeps the incoming order (missing-fields-first from the parent)
    return arr;
  };

  // Group achievements by Base Game / DLCs / Legacy Xbox Challenges.
  const groups = useMemo(() => {
    const baseAchievements = [];
    const challengeAchievements = [];
    const dlcOrder = [];
    const dlcMap = new Map();
    for (const ach of achievements) {
      if (ach.isChallenge) {
        challengeAchievements.push(ach);
      } else if (!ach.dlcId) {
        baseAchievements.push(ach);
      } else {
        if (!dlcMap.has(ach.dlcId)) {
          dlcMap.set(ach.dlcId, { key: `dlc-${ach.dlcId}`, name: ach.dlcName || "DLC", isDlc: true, achievements: [] });
          dlcOrder.push(ach.dlcId);
        }
        dlcMap.get(ach.dlcId).achievements.push(ach);
      }
    }
    const result = [];
    if (baseAchievements.length > 0) {
      result.push({ key: "base", name: "Base Game", isDlc: false, achievements: sortWithin(baseAchievements) });
    }
    let dlcGroups = dlcOrder.map((id) => dlcMap.get(id));
    if (sortDlcByName) dlcGroups = [...dlcGroups].sort((a, b) => nameCompare(a, b));
    for (const g of dlcGroups) result.push({ ...g, achievements: sortWithin(g.achievements) });
    if (challengeAchievements.length > 0) {
      result.push({ key: "challenges", name: "Legacy Xbox Challenges", isDlc: false, isChallenge: true, achievements: sortWithin(challengeAchievements) });
    }
    return result;
  }, [achievements, sortMode, sortDlcByName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-purple-400">
            <CheckSquare className="w-5 h-5" />
            Manual Achievement Entry
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Manually mark achievements as unlocked or locked, and adjust unlock dates/times. All achievements are shown — including ones already unlocked.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Quick Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={onSelectAll}
              className="text-xs border-green-500/30 text-green-400 hover:bg-green-500/10"
            >
              <CheckSquare className="w-3 h-3 mr-1" />
              Select All
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onDeselectAll}
              className="text-xs border-white/20"
            >
              <Square className="w-3 h-3 mr-1" />
              Deselect All
            </Button>
            <span className="text-xs text-white/50 ml-auto">
              {achievements.length} total achievements
            </span>
          </div>

          {/* Sort controls */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="text-white/40">Sort:</span>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              className="bg-white/5 border border-white/15 rounded px-2 py-1 text-white/80 focus:outline-none focus:border-purple-400/50"
              data-testid="manual-sync-sort-mode"
            >
              <option value="status" className="bg-[#0A0A0A]">Needs attention</option>
              <option value="unlockedFirst" className="bg-[#0A0A0A]">Unlocked first</option>
              <option value="lockedFirst" className="bg-[#0A0A0A]">Locked first</option>
              <option value="name" className="bg-[#0A0A0A]">Name (A–Z)</option>
            </select>
            <label className="inline-flex items-center gap-1.5 text-white/60 cursor-pointer select-none" data-testid="manual-sync-sort-dlc-name">
              <input
                type="checkbox"
                checked={sortDlcByName}
                onChange={(e) => setSortDlcByName(e.target.checked)}
                className="accent-purple-500"
              />
              Sort DLC by name
            </label>
          </div>
          
          {/* Achievement List — grouped by Base Game / DLCs */}
          <div className="flex-1 overflow-y-auto space-y-3 min-h-0 border border-white/10 rounded-lg p-2">
            {achievements.length > 0 ? (
              groups.map((group) => {
                const isCollapsed = collapsed[group.key];
                const groupIds = group.achievements.map(a => a.id);
                const groupSelectedCount = groupIds.filter(id => selections[id]?.selected).length;
                const allSelected = groupSelectedCount > 0 && groupSelectedCount === groupIds.length;
                const handleToggleGroupSelection = () => {
                  if (allSelected) {
                    groupIds.forEach(id => {
                      if (selections[id]?.selected) onToggleAchievement(id);
                    });
                  } else {
                    groupIds.forEach(id => {
                      if (!selections[id]?.selected) onToggleAchievement(id);
                    });
                  }
                };
                return (
                  <section
                    key={group.key}
                    data-testid={`manual-sync-group-${group.key}`}
                    className={`rounded-md border ${group.isChallenge ? "border-amber-500/30 bg-amber-500/[0.04]" : group.isDlc ? "border-amber-500/20 bg-amber-500/[0.02]" : "border-purple-500/20 bg-purple-500/[0.02]"}`}
                  >
                    <header
                      className={`sticky top-0 z-10 flex items-center gap-2 px-2 py-1.5 border-b ${group.isChallenge ? "border-amber-500/30 bg-[#0a0a0a]" : group.isDlc ? "border-amber-500/20 bg-[#0a0a0a]" : "border-purple-500/20 bg-[#0a0a0a]"} rounded-t-md`}
                    >
                      <button
                        type="button"
                        onClick={() => setCollapsed(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
                        className="text-white/60 hover:text-white"
                        data-testid={`manual-sync-toggle-${group.key}`}
                        aria-label={isCollapsed ? "Expand group" : "Collapse group"}
                      >
                        {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      {group.isChallenge ? (
                        <Package className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      ) : group.isDlc ? (
                        <Package className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      ) : (
                        <Trophy className="w-4 h-4 text-purple-400 flex-shrink-0" />
                      )}
                      <h4 className={`text-sm font-medium truncate ${group.isChallenge ? "text-amber-300" : group.isDlc ? "text-amber-300" : "text-purple-300"}`}>
                        {group.name}
                      </h4>
                      {group.isChallenge && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300/80 border border-amber-500/30 flex-shrink-0">
                          Legacy
                        </span>
                      )}
                      <span className="text-xs text-white/40 flex-shrink-0">
                        {groupSelectedCount}/{groupIds.length}
                      </span>
                      <button
                        type="button"
                        onClick={handleToggleGroupSelection}
                        className="ml-auto text-[11px] text-white/50 hover:text-white px-1.5 py-0.5 rounded border border-white/10 hover:border-white/30 flex-shrink-0"
                        data-testid={`manual-sync-group-select-${group.key}`}
                      >
                        {allSelected ? "Deselect group" : "Select group"}
                      </button>
                    </header>
                    {!isCollapsed && (
                      <div className="p-2 space-y-2">
                        {group.achievements.map((ach) => (
                <div
                  key={ach.id}
                  className={`p-3 rounded-lg border transition-colors ${
                    selections[ach.id]?.selected 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-white/5 border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <button
                      onClick={() => onToggleAchievement(ach.id)}
                      className="mt-1"
                    >
                      {selections[ach.id]?.selected ? (
                        <CheckSquare className="w-5 h-5 text-green-400" />
                      ) : (
                        <Square className="w-5 h-5 text-white/40" />
                      )}
                    </button>
                    
                    {/* Achievement Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {ach.imageUrl && (
                          <img 
                            src={ach.imageUrl} 
                            alt="" 
                            className="w-8 h-8 rounded object-contain bg-black/50"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white truncate">{ach.name}</p>
                            {ach.isUnlocked && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded">
                                Unlocked
                              </span>
                            )}
                            {ach.score > 0 && (
                              <span className="text-[10px] text-yellow-400">
                                {ach.score} GS
                              </span>
                            )}
                          </div>
                          {ach.description && (
                            <p className="text-xs text-white/50 truncate">{ach.description}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Date/Time/Offline inputs when selected */}
                      {selections[ach.id]?.selected && (
                        <div className="mt-2 space-y-2 p-2 bg-black/30 rounded border border-white/10">
                          {/* Date Input */}
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-white/50 w-14">Date:</Label>
                            <Input
                              type="text"
                              value={selections[ach.id]?.dateText || ''}
                              onChange={(e) => {
                                const formatted = formatDateInput(e.target.value);
                                let earnedAt = null;
                                if (formatted.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                                  const [month, day, year] = formatted.split('/');
                                  const parsed = new Date(`${year}-${month}-${day}T12:00:00`);
                                  if (!isNaN(parsed.getTime())) {
                                    earnedAt = parsed.toISOString();
                                  }
                                }
                                onSelectionsChange(prev => ({
                                  ...prev,
                                  [ach.id]: {
                                    ...prev[ach.id],
                                    dateText: formatted,
                                    earnedAt: earnedAt,
                                    unlockDate: earnedAt ? `${formatted.split('/')[2]}-${formatted.split('/')[0]}-${formatted.split('/')[1]}` : prev[ach.id]?.unlockDate,
                                  }
                                }));
                              }}
                              className="h-7 text-xs bg-black/50 border-white/10 flex-1"
                              placeholder="MM/DD/YYYY"
                              maxLength={10}
                            />
                          </div>
                          
                          {/* Time Input */}
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-white/50 w-14">Time:</Label>
                            <Input
                              type="text"
                              value={selections[ach.id]?.earnedTime || ''}
                              onChange={(e) => {
                                const formatted = formatTimeInput(e.target.value);
                                onSelectionsChange(prev => ({
                                  ...prev,
                                  [ach.id]: {
                                    ...prev[ach.id],
                                    earnedTime: formatted,
                                  }
                                }));
                              }}
                              className="h-7 text-xs bg-black/50 border-white/10 flex-1"
                              placeholder="HH:MM (optional)"
                              maxLength={5}
                            />
                            {selections[ach.id]?.earnedTime && 
                             /^\d{1,2}:\d{2}$/.test(selections[ach.id].earnedTime) && (
                              <span className="text-xs text-green-400">
                                ({formatTime12Hour(selections[ach.id].earnedTime)})
                              </span>
                            )}
                          </div>
                          
                          {/* Offline Checkbox */}
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`manual-offline-${ach.id}`}
                              checked={selections[ach.id]?.unlockedOffline || false}
                              onCheckedChange={(checked) => {
                                onSelectionsChange(prev => ({
                                  ...prev,
                                  [ach.id]: {
                                    ...prev[ach.id],
                                    unlockedOffline: checked,
                                  }
                                }));
                              }}
                              className="border-amber-500/50 data-[state=checked]:bg-amber-500"
                            />
                            <Label htmlFor={`manual-offline-${ach.id}`} className="text-xs text-amber-200 cursor-pointer">
                              Unlocked Offline
                            </Label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                );
              })
            ) : (
              <p className="text-center text-white/40 py-8">No achievements found for this game</p>
            )}
          </div>
          
          {/* Selection Summary */}
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg px-4 py-2 text-sm">
            <span className="text-white/70">Selected: </span>
            <span className="text-purple-400 font-medium">
              {Object.values(selections).filter(v => v.selected).length} / {achievements.length}
            </span>
            <span className="text-white/50"> • </span>
            <span className="text-white/70">Score: </span>
            <span className="text-purple-400 font-medium">
              {achievements
                .filter(a => selections[a.id]?.selected)
                .reduce((sum, a) => sum + (a.score || 0), 0)} GS
            </span>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-white/20"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={onSave}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Score Mismatch Dialog
 *
 * Used by the Xbox / PlayStation / Steam sync flows when the platform
 * reports more unlocks (or more gamerscore / trophy points / achievements)
 * than the local DB tracks. Renders a label that matches the platform's
 * terminology — "X GS" for Xbox, "X Trophy points" for PSN, "X achievements"
 * for Steam.
 */
export function ScoreMismatchDialog({
  open,
  onOpenChange,
  scoreDifference,
  countDifference,
  platformLabel,
  scoreUnit,
  missingAchievements,
  selections,
  onSelectionsChange,
  onSelectAll,
  onDeselectAll,
  onSave,
  saving,
}) {
  const label = platformLabel || "Xbox";
  const unit = scoreUnit || "GS";
  const hasScoreGap = (scoreDifference || 0) > 0;
  const hasCountGap = (countDifference || 0) > 0;
  // Headline: prefer the score gap when both exist; fall back to count.
  const headlineNumber = hasScoreGap ? scoreDifference : countDifference;
  const headlineUnit = hasScoreGap ? unit : (countDifference === 1 ? "achievement" : "achievements");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-400">
            <Trophy className="w-5 h-5" />
            Score Mismatch Detected
          </DialogTitle>
          <DialogDescription className="text-white/60">
            {label} reports more {hasScoreGap ? unit.toLowerCase() : "achievements"} earned than what&apos;s tracked. Select the achievements you&apos;ve unlocked to fix the mismatch. These will be marked as &quot;score-verified&quot; since they match your reported total.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Score Info */}
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-4 py-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-white/70">Missing {hasScoreGap ? "Score" : "Count"}:</span>
              <span className="text-orange-400 font-bold text-lg">
                {headlineNumber} {headlineUnit}
              </span>
            </div>
            {hasScoreGap && hasCountGap && (
              <div className="flex items-center justify-between mt-1 text-xs">
                <span className="text-white/50">Trophies/Achievements short:</span>
                <span className="text-orange-300/80">{countDifference}</span>
              </div>
            )}
            <p className="text-xs text-white/50 mt-1">
              Select the achievements you&apos;ve unlocked to fix the mismatch
            </p>
          </div>
          
          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onSelectAll}
              className="text-xs border-green-500/30 text-green-400 hover:bg-green-500/10"
            >
              <CheckSquare className="w-3 h-3 mr-1" />
              Select All
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onDeselectAll}
              className="text-xs border-white/20"
            >
              <Square className="w-3 h-3 mr-1" />
              Deselect All
            </Button>
          </div>
          
          {/* Locked Achievements List — sorted alphabetically by name
              (case-insensitive) so users can quickly find the one they want.
              Memoised so the order is stable across re-renders triggered by
              date/time/checkbox input. */}
          <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
            {missingAchievements.length === 0 ? (
              <p className="text-center text-white/40 py-8">
                All achievements are already unlocked
              </p>
            ) : (
              [...missingAchievements]
                .sort((a, b) =>
                  (a?.name || "").localeCompare(b?.name || "", undefined, { sensitivity: "base" })
                )
                .map((ach) => (
                <div
                  key={ach.id}
                  className={`p-3 rounded-lg border transition-colors ${
                    selections[ach.id]?.selected
                      ? 'bg-green-500/10 border-green-500/30'
                      : 'bg-white/5 border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => {
                        onSelectionsChange(prev => ({
                          ...prev,
                          [ach.id]: {
                            ...prev[ach.id],
                            selected: !prev[ach.id]?.selected,
                          }
                        }));
                      }}
                      className="mt-1"
                    >
                      {selections[ach.id]?.selected ? (
                        <CheckSquare className="w-5 h-5 text-green-400" />
                      ) : (
                        <Square className="w-5 h-5 text-white/40" />
                      )}
                    </button>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {ach.imageUrl && (
                          <img 
                            src={ach.imageUrl} 
                            alt="" 
                            className="w-8 h-8 rounded object-contain bg-black/50"
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-white">{ach.name}</p>
                            <span className="text-xs text-yellow-400">
                              {ach.score > 0 ? `${ach.score} ${unit === "Trophy points" ? "pts" : unit}` : ach.trophyType || ""}
                            </span>
                            {(ach.isDlc || ach.dlcName) && (
                              <span
                                className="px-1.5 py-0.5 text-[9px] uppercase tracking-wide rounded bg-purple-500/20 text-purple-200 border border-purple-300/30"
                                title={ach.dlcName || "DLC achievement"}
                              >
                                DLC{ach.dlcName ? `: ${ach.dlcName}` : ""}
                              </span>
                            )}
                          </div>
                          {ach.description && (
                            <p className="text-xs text-white/50 truncate">{ach.description}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Date/Time/Offline inputs when selected */}
                      {selections[ach.id]?.selected && (
                        <div className="mt-2 space-y-2 p-2 bg-black/30 rounded border border-white/10">
                          {/* Date Input */}
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-white/50 w-14">Date:</Label>
                            <Input
                              type="text"
                              value={selections[ach.id]?.dateText || ''}
                              onChange={(e) => {
                                const formatted = formatDateInput(e.target.value);
                                let earnedAt = null;
                                if (formatted.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                                  const [month, day, year] = formatted.split('/');
                                  const parsed = new Date(`${year}-${month}-${day}T12:00:00`);
                                  if (!isNaN(parsed.getTime())) {
                                    earnedAt = parsed.toISOString();
                                  }
                                }
                                onSelectionsChange(prev => ({
                                  ...prev,
                                  [ach.id]: {
                                    ...prev[ach.id],
                                    dateText: formatted,
                                    earnedAt: earnedAt,
                                  }
                                }));
                              }}
                              className="h-7 text-xs bg-black/50 border-white/10 flex-1"
                              placeholder="MM/DD/YYYY"
                              maxLength={10}
                            />
                          </div>
                          
                          {/* Time Input */}
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-white/50 w-14">Time:</Label>
                            <Input
                              type="text"
                              value={selections[ach.id]?.earnedTime || ''}
                              onChange={(e) => {
                                const formatted = formatTimeInput(e.target.value);
                                onSelectionsChange(prev => ({
                                  ...prev,
                                  [ach.id]: {
                                    ...prev[ach.id],
                                    earnedTime: formatted,
                                  }
                                }));
                              }}
                              className="h-7 text-xs bg-black/50 border-white/10 flex-1"
                              placeholder="HH:MM (optional)"
                              maxLength={5}
                            />
                            {selections[ach.id]?.earnedTime && 
                             /^\d{1,2}:\d{2}$/.test(selections[ach.id].earnedTime) && (
                              <span className="text-xs text-green-400">
                                ({formatTime12Hour(selections[ach.id].earnedTime)})
                              </span>
                            )}
                          </div>
                          
                          {/* Offline Checkbox */}
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`mismatch-offline-${ach.id}`}
                              checked={selections[ach.id]?.unlockedOffline || false}
                              onCheckedChange={(checked) => {
                                onSelectionsChange(prev => ({
                                  ...prev,
                                  [ach.id]: {
                                    ...prev[ach.id],
                                    unlockedOffline: checked,
                                  }
                                }));
                              }}
                              className="border-amber-500/50 data-[state=checked]:bg-amber-500"
                            />
                            <Label htmlFor={`mismatch-offline-${ach.id}`} className="text-xs text-amber-200 cursor-pointer">
                              Unlocked Offline
                            </Label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Selection Summary */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-2 text-sm">
            {hasScoreGap ? (
              <>
                <span className="text-white/70">Selected {unit === "Trophy points" ? "Points" : "Score"}: </span>
                <span className="text-green-400 font-medium">
                  {missingAchievements
                    .filter(a => selections[a.id]?.selected)
                    .reduce((sum, a) => sum + (a.score || 0), 0)} {unit === "Trophy points" ? "pts" : unit}
                </span>
                <span className="text-white/50"> / {scoreDifference} {unit === "Trophy points" ? "pts" : unit} missing</span>
              </>
            ) : (
              <>
                <span className="text-white/70">Selected: </span>
                <span className="text-green-400 font-medium">
                  {missingAchievements.filter(a => selections[a.id]?.selected).length}
                </span>
                <span className="text-white/50"> / {countDifference} {countDifference === 1 ? "achievement" : "achievements"} missing</span>
              </>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-white/20"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={onSave}
              disabled={saving || Object.values(selections).filter(v => v.selected).length === 0}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Verify &amp; Mark Selected as Unlocked
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Xbox Title Search Dialog
 */
export function XboxTitleSearchDialog({
  open,
  onOpenChange,
  searchQuery,
  onSearchQueryChange,
  onSearch,
  searching,
  results,
  onSelect,
  linking,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-400">
            <Gamepad2 className="w-5 h-5" />
            Link Xbox Game
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Search for this game in your Xbox library to enable achievement sync
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Search */}
          <div className="flex gap-2">
            <Input
              placeholder="Search game name..."
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onSearch()}
              className="bg-black/50 border-white/10 flex-1"
            />
            <Button
              onClick={onSearch}
              disabled={searching || !searchQuery}
              className="bg-green-600 hover:bg-green-500"
            >
              {searching ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </Button>
          </div>
          
          {/* Results */}
          <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
            {results.length > 0 ? (
              results.map((game) => (
                <button
                  key={game.titleId}
                  onClick={() => onSelect(game)}
                  disabled={linking}
                  className="w-full p-3 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    {game.displayImage && (
                      <img 
                        src={game.displayImage} 
                        alt="" 
                        className="w-12 h-12 rounded object-cover bg-black/50"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{game.name}</p>
                      <div className="flex items-center gap-2 text-xs text-white/50">
                        <span>{game.currentGamerscore || 0} / {game.maxGamerscore || 0} GS</span>
                        <span>•</span>
                        <span>{game.earnedAchievements || 0} achievements</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))
            ) : searchQuery && !searching ? (
              <p className="text-center text-white/40 py-8">No games found</p>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * PSN Title Search Dialog
 */
export function PsnTitleSearchDialog({
  open,
  onOpenChange,
  searchQuery,
  onSearchQueryChange,
  onSearch,
  searching,
  results,
  onSelect,
  linking,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-400">
            <Gamepad2 className="w-5 h-5" />
            Link PlayStation Game
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Search for this game in your PlayStation trophy list to enable trophy sync
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Search */}
          <div className="flex gap-2">
            <Input
              placeholder="Search game name..."
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onSearch()}
              className="bg-black/50 border-white/10 flex-1"
            />
            <Button
              onClick={onSearch}
              disabled={searching || !searchQuery}
              className="bg-blue-600 hover:bg-blue-500"
            >
              {searching ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </Button>
          </div>
          
          {/* Results */}
          <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
            {results.length > 0 ? (
              results.map((game) => (
                <button
                  key={game.npCommunicationId}
                  onClick={() => onSelect(game)}
                  disabled={linking}
                  className="w-full p-3 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    {game.trophyTitleIconUrl && (
                      <img 
                        src={game.trophyTitleIconUrl} 
                        alt="" 
                        className="w-12 h-12 rounded object-cover bg-black/50"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{game.trophyTitleName}</p>
                      <div className="flex items-center gap-2 text-xs text-white/50">
                        <span>{game.progress || 0}% complete</span>
                        <span>•</span>
                        <span>{game.earnedTrophies?.total || 0} / {game.definedTrophies?.total || 0} trophies</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))
            ) : searchQuery && !searching ? (
              <p className="text-center text-white/40 py-8">No games found</p>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Exophase Verify Dialog
 * Verifies achievements via the user's Exophase profile.
 */
export function ExophaseVerifyDialog({
  open,
  onOpenChange,
  game,
  onVerified,
}) {
  const [username, setUsername] = useState(() => localStorage.getItem("exophaseUsername") || "");
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleVerify = async () => {
    if (!username.trim()) return;
    setVerifying(true);
    setResult(null);
    setError(null);
    localStorage.setItem("exophaseUsername", username.trim());

    try {
      const { data } = await api.post(`/api/exophase/verify-game`, {
        gameId: game.id,
        exophaseUsername: username.trim(),
      });
      setResult(data);
      if (data.success && (data.newlyVerified > 0 || data.newlyUnlocked > 0)) {
        onVerified?.();
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Verification failed";
      setError(detail);
    } finally {
      setVerifying(false);
    }
  };

  const handleClose = (val) => {
    if (!verifying) {
      setResult(null);
      setError(null);
      onOpenChange(val);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg" data-testid="exophase-verify-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <CheckSquare className="w-5 h-5" />
            Verify via Exophase
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Cross-reference your unlocked achievements with your Exophase profile to mark them as secondary verified.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Username input — Verify button is hidden after a successful
              match to prevent accidentally re-triggering the 15-30s search.
              When the user wants to re-run, they can use the "Re-verify"
              button in the footer. */}
          <div className="space-y-2">
            <Label className="text-white/70 text-sm">Exophase Username</Label>
            <div className="flex gap-2">
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="e.g. MrAspirin"
                className="bg-zinc-900 border-white/10 text-white"
                disabled={verifying || (result && result.success)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !(result && result.success)) {
                    handleVerify();
                  }
                }}
                data-testid="exophase-username-input"
              />
              {!(result && result.success) && (
                <Button
                  onClick={handleVerify}
                  disabled={!username.trim() || verifying}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-medium shrink-0"
                  data-testid="exophase-verify-btn"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Verify"
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Loading state */}
          {verifying && (
            <div className="text-center py-6 space-y-3">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-400" />
              <p className="text-white/50 text-sm">
                Searching Exophase for "{game?.name}"...
                <br />
                <span className="text-white/30 text-xs">This may take 15-30 seconds</span>
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="exophase-verify-error">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-3" data-testid="exophase-verify-results">
              {result.success ? (
                <>
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                    <p className="text-emerald-300 text-sm font-medium flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      Matched: {result.exophaseGame} ({result.matchConfidence}% confidence)
                      {result.platformUsed && (
                        <span className="text-emerald-400/60 text-xs ml-1">via {result.platformUsed}</span>
                      )}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-zinc-800/50 rounded p-2 text-center">
                      <p className="text-lg font-bold text-amber-400">{result.newlyVerified}</p>
                      <p className="text-xs text-white/40">Newly Verified</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded p-2 text-center">
                      <p className="text-lg font-bold text-emerald-400">{result.alreadyVerified}</p>
                      <p className="text-xs text-white/40">Already Verified</p>
                    </div>
                    {result.newlyUnlocked > 0 && (
                      <div className="bg-zinc-800/50 rounded p-2 text-center">
                        <p className="text-lg font-bold text-blue-400">{result.newlyUnlocked}</p>
                        <p className="text-xs text-white/40">Newly Unlocked</p>
                      </div>
                    )}
                    <div className="bg-zinc-800/50 rounded p-2 text-center">
                      <p className="text-lg font-bold text-zinc-400">{result.notMatched}</p>
                      <p className="text-xs text-white/40">Not Matched</p>
                    </div>
                  </div>

                  <p className="text-white/50 text-xs text-center">
                    {result.earnedOnExophase} of {result.totalOnExophase} earned on Exophase
                  </p>
                </>
              ) : (
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                  <p className="text-orange-300 text-sm">{result.message}</p>
                  {result.gamesFound?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-white/40 text-xs mb-1">Games found on profile:</p>
                      <div className="max-h-32 overflow-y-auto text-xs text-white/50 space-y-0.5">
                        {result.gamesFound.map((g, i) => (
                          <div key={i}>{"\u2022"} {g}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer — gives users an explicit way to commit/close instead of
            hunting for the X. Buttons are state-aware:
              • Idle / not yet verified → just "Cancel"
              • Verifying → "Cancel" disabled (search is in flight)
              • Success → "Done" (primary) + "Re-verify" (secondary)
              • Failure or error → "Try Again" + "Close" */}
        <DialogFooter className="pt-2 gap-2 sm:gap-2 flex-row justify-end">
          {result?.success ? (
            <>
              <Button
                variant="outline"
                onClick={handleVerify}
                disabled={verifying || !username.trim()}
                className="bg-transparent border-white/15 text-white/70 hover:bg-white/5"
                data-testid="exophase-reverify-btn"
              >
                {verifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Re-verifying...
                  </>
                ) : (
                  "Re-verify"
                )}
              </Button>
              <Button
                onClick={() => handleClose(false)}
                className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium"
                data-testid="exophase-done-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Done
              </Button>
            </>
          ) : result || error ? (
            <>
              <Button
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={verifying}
                className="bg-transparent border-white/15 text-white/70 hover:bg-white/5"
                data-testid="exophase-close-btn"
              >
                Close
              </Button>
              <Button
                onClick={handleVerify}
                disabled={!username.trim() || verifying}
                className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
                data-testid="exophase-tryagain-btn"
              >
                {verifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Try Again"
                )}
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={verifying}
              className="bg-transparent border-white/15 text-white/70 hover:bg-white/5"
              data-testid="exophase-cancel-btn"
            >
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
